import homework from '@/api/eschool/teacher-homework'
import { baseState, baseMutations } from '@/store/mixins'
import AcademicYear from '@/api/academic-year'
import ProgramMain from '@/api/main-program'
import ProgramSub from '@/api/sub-program'
import AcademicClass from '@/api/class-operation'
import Terms from '@/api/term-semester'
import moment from 'moment'

export const state = {
  ...baseState,
  edit_data: {},
  date_from: moment()
    .add(-7, 'Day')
    .format('DD-MM-YYYY'),
  date_to: moment().format('DD-MM-YYYY'),
  status: 1,
  direction: 'ASC',
  files: [],
  academicYears: [],
  programMains: [],
  programSubs: [],
  classes: [],
  terms: []
}

export const getters = {}

export const mutations = {
  ...baseMutations,
  SET_EDIT_DATA(state, data) {
    state.edit_data = data
  },
  SET_DATE_FROM(state, date) {
    state.date_from = date
  },
  SET_DATE_TO(state, date) {
    state.date_to = date
  },
  SET_STATUS_VALUE(state, value) {
    state.status = value
  },
  SET_FILES(state, file) {
    state.files = file
  },
  RECEIVE_ACADEMIC_YEARS(state, data) {
    state.academicYears = data
  },
  RECEIVE_ACADEMIC_PROGRAM_MAINS(state, data) {
    state.programMains = data
  },
  RECEIVE_ACADEMIC_PROGRAM_SUBS(state, data) {
    state.programSubs = data
  },
  RECEIVE_CLASSES(state, data) {
    state.classes = data
  }
}

export const actions = {
  fetch(context, attrubutes = {}) {
    let params = {
      page: context.state.pagination.page,
      perPage: context.state.pagination.perPage,
      search: context.state.search,
      date_from: context.state.date_from,
      date_to: context.state.date_to,
      status: context.state.status,
      filter: {
        branch_id: attrubutes.branch_id,
        ac_year_id: attrubutes.ac_year_id,
        acp_id: attrubutes.acp_id,
        acps_id: attrubutes.acps_id,
        ac_term_id: attrubutes.ac_term_id,
        class_id: attrubutes.class_id
      }
    }

    return homework.get({ params }).then(response => {
      context.commit('RECEIVE_RESOURCES', response)
      return Promise.resolve(response)
    })
  },
  store(context, data) {
    return homework.store(data)
  },
  update(context, data) {
    return homework.update(data)
  },
  changeStatus(context, { id, data }) {
    homework.changeStatus(id, data)
  },

  async find(context, id) {
    if (_.isEmpty(context.state.edit_data)) {
      let response = await homework.find(id)
      context.commit('SET_EDIT_DATA', response.data)
    }
  },

  getSubject(context, { class_id, teacher_id }) {
    return homework.getSubject(class_id, {
      params: {
        teacher_id: teacher_id
      }
    })
  },

  // teacher_id is passed from the form when staff is selecting;
  // for teacher login, backend ignores it and uses auth user
  getClass(context, teacher_id) {
    return homework.getClass({
      params: {
        teacher_id: teacher_id
      }
    })
  },

  // Clean call — /api/homeworks/queries/teachers handles
  // filtering internally based on auth guard (staff vs teacher)
  getTeacher(context) {
    return homework.getTeacher()
  },

  removeFileUploaded(context, attributes) {
    return homework.removeFile({
      params: attributes
    })
  },
  sendNotification(context, homework_id) {
    return homework.sendNotification(homework_id)
  },
  getFile(context, homework_id) {
    let params = {
      sort: 'updated_at',
      direction: context.state.direction
    }
    return homework.getFile(homework_id, { params }).then(response => {
      let files = response.files.data
      context.commit('SET_FILES', files)
    })
  },
  deleteItem(context, homework_att_id) {
    return homework.deleteItem(homework_att_id)
  },

  fetchAcademicYear(context, attributes = {}) {
    return AcademicYear.get({
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      },
      params: {
        page: -1,
        fields: {
          academic_years: 'ac_year_id,ac_year,ac_status_id'
        },
        ...attributes
      }
    }).then(response => {
      context.commit('RECEIVE_ACADEMIC_YEARS', response.data)
      return Promise.resolve(response)
    })
  },
  fetchProgramMain(context, attributes = {}) {
    return ProgramMain.get({
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      },
      params: {
        page: -1,
        fields: {
          academic_program_mains: 'acp_id,acp_name_native,acp_name_foreign'
        },
        ...attributes
      }
    }).then(response => {
      context.commit('RECEIVE_ACADEMIC_PROGRAM_MAINS', response.data)
      return Promise.resolve(response)
    })
  },
  fetchProgramSub(context, attributes = {}) {
    const { acp_id, ...rest } = attributes

    return ProgramSub.get({
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      },
      params: {
        page: -1,
        fields: {
          academic_program_subs:
            'acps_id,acp_id,acps_name_native,acps_name_foreign'
        },
        filter: {
          acp_id: acp_id
        },
        ...rest
      }
    }).then(response => {
      context.commit('RECEIVE_ACADEMIC_PROGRAM_SUBS', response.data)
      return Promise.resolve(response)
    })
  },
  fetchClass(context, attributes = {}) {
    const { ac_term_id, ...rest } = attributes

    return AcademicClass.get({
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      },
      params: {
        page: -1,
        fields: {
          academic_classes: 'class_id,class_name,ac_status_id,acps_id'
        },
        filter: {
          ac_term_id: ac_term_id,
          ac_status_id: [1, 3]
        },
        include: 'academicStatus,subProgram',
        ...rest
      }
    }).then(response => {
      context.commit('RECEIVE_CLASSES', response.data)
      return Promise.resolve(response)
    })
  },
  fetchTerms(context, attributes = {}) {
    const { ac_year_id, acps_id, ...rest } = attributes

    return Terms.get({
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      },
      params: {
        page: -1,
        ac_year_id: ac_year_id,
        acps_id: acps_id,
        ...rest
      },
      paramsSerializer: params => {
        return Object.entries(params)
          .flatMap(([key, value]) =>
            Array.isArray(value)
              ? value.map(v => `${key}[]=${v}`)
              : [`${key}=${value}`]
          )
          .join('&')
      }
    }).then(response => {
      context.commit('RECEIVE_TERMS', response.data)
      return Promise.resolve(response)
    })
  },
  getAcademicYear(context, attributes = {}) {
    return homework.academicYear({ params: attributes })
  }
}